import { useState } from 'react';

interface FooterProps {
  isFormOpen: boolean;
  setIsFormOpen: (open: boolean) => void;
}

const Footer = ({ isFormOpen, setIsFormOpen }: FooterProps) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    meetingRequest: 'no',
    meetingDate1: '',
    meetingDate2: '',
    meetingDate3: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      alert('お名前とメールアドレスは必須項目です。');
      return;
    }

    if (formData.meetingRequest === 'yes' && (!formData.meetingDate1 || !formData.meetingDate2 || !formData.meetingDate3)) {
      alert('面談希望の場合は、候補日時を3つ入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('company', formData.company);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);
      formBody.append('meetingRequest', formData.meetingRequest === 'yes' ? '面談希望：はい' : '面談希望：いいえ');
      
      if (formData.meetingRequest === 'yes') {
        formBody.append('meetingDate1', `候補日時1: ${formData.meetingDate1}`);
        formBody.append('meetingDate2', `候補日時2: ${formData.meetingDate2}`);
        formBody.append('meetingDate3', `候補日時3: ${formData.meetingDate3}`);
      }

      const response = await fetch('https://readdy.ai/api/form/d4ijn3aeh8g2mrj4aq6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', company: '', phone: '', message: '', meetingRequest: 'no', meetingDate1: '', meetingDate2: '', meetingDate3: '' });
        setTimeout(() => {
          setIsFormOpen(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <footer id="contact" className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8 mb-12">
            {/* 会社情報 */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Pacifico, serif' }}>
                Autocrove
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                業務自動化・AI活用支援を専門とする企業です。<br />
                Google Apps Script、AI、LINE連携を活用し、<br />
                お客様の業務効率化を強力にサポートします。
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="ri-building-line text-blue-400 mr-3"></i>
                  <span className="text-gray-300">事業内容：業務自動化／AI連携／LINE構築／GAS開発</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line text-blue-400 mr-3"></i>
                  <span className="text-gray-300">お問い合わせ：24時間受付中</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-time-line text-blue-400 mr-3"></i>
                  <span className="text-gray-300">対応時間：平日9:00-18:00</span>
                </div>
              </div>
            </div>

            {/* サービス */}
            <div>
              <h4 className="text-lg font-semibold mb-4">サービス</h4>
              <ul className="space-y-2">
                <li>
                  <button 
                    onClick={() => scrollToSection('features')}
                    className="text-gray-300 hover:text-blue-400 transition-colors cursor-pointer"
                  >
                    業務自動化
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('strengths')}
                    className="text-gray-300 hover:text-blue-400 transition-colors cursor-pointer"
                  >
                    AI連携システム
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => scrollToSection('case-studies')}
                    className="text-gray-300 hover:text-blue-400 transition-colors cursor-pointer"
                  >
                    LINE Bot開発
                  </button>
                </li>
                <li>
                  <span className="text-gray-300">GAS開発</span>
                </li>
                <li>
                  <span className="text-gray-300">運用サポート</span>
                </li>
              </ul>
            </div>

            {/* お問い合わせ */}
            <div>
              <h4 className="text-lg font-semibold mb-4">お問い合わせ</h4>
              <div className="space-y-4">
                <button 
                  onClick={() => setIsFormOpen(true)}
                  className="w-full bg-red-500 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  無料相談はこちら
                </button>
                
                <div className="flex space-x-4">
                  <button className="w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line"></i>
                  </button>
                  <button className="w-10 h-10 bg-blue-800 rounded-full flex items-center justify-center hover:bg-blue-900 transition-colors cursor-pointer">
                    <i className="ri-facebook-line"></i>
                  </button>
                  <button className="w-10 h-10 bg-pink-600 rounded-full flex items-center justify-center hover:bg-pink-700 transition-colors cursor-pointer">
                    <i className="ri-instagram-line"></i>
                  </button>
                  <button className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center hover:bg-green-700 transition-colors cursor-pointer">
                    <i className="ri-line-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* 導入実績 */}
          <div className="border-t border-gray-700 pt-8 mb-8">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-400 mb-1">50+</div>
                <div className="text-gray-400 text-sm">導入実績</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-400 mb-1">90%</div>
                <div className="text-gray-400 text-sm">工数削減率</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-400 mb-1">95%</div>
                <div className="text-gray-400 text-sm">継続率</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-400 mb-1">3ヶ月</div>
                <div className="text-gray-400 text-sm">平均ROI回収期間</div>
              </div>
            </div>
          </div>

          {/* コピーライト */}
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2024 Autocrove. All rights reserved.
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">
                プライバシーポリシー
              </button>
              <button className="text-gray-400 hover:text-white text-sm transition-colors cursor-pointer">
                利用規約
              </button>
              <a 
                href="https://readdy.ai/?origin=logo" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-sm transition-colors"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* フォームモーダル */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">無料相談お申し込み</h3>
                <button
                  onClick={() => setIsFormOpen(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">お申し込みありがとうございます</h4>
                  <p className="text-gray-600">担当者より3営業日以内にご連絡いたします。</p>
                </div>
              ) : (
                <form id="footer-contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="山田 太郎"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="example@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      会社名
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="株式会社〇〇"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      電話番号
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="03-1234-5678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ご相談内容
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="ご相談内容をご記入ください（500文字以内）"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.message.length}/500
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      面談希望 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="meetingRequest"
                      value={formData.meetingRequest}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="no">いいえ</option>
                      <option value="yes">はい</option>
                    </select>
                  </div>

                  {formData.meetingRequest === 'yes' && (
                    <div className="space-y-4 bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-gray-700 mb-3">
                        面談希望日時（3つの候補をご入力ください）
                      </p>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          候補日時 1 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="meetingDate1"
                          value={formData.meetingDate1}
                          onChange={handleInputChange}
                          required={formData.meetingRequest === 'yes'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="例：2024年12月25日 14:00-15:00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          候補日時 2 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="meetingDate2"
                          value={formData.meetingDate2}
                          onChange={handleInputChange}
                          required={formData.meetingRequest === 'yes'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="例：2024年12月26日 10:00-11:00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          候補日時 3 <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="meetingDate3"
                          value={formData.meetingDate3}
                          onChange={handleInputChange}
                          required={formData.meetingRequest === 'yes'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="例：2024年12月27日 15:00-16:00"
                        />
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                      送信に失敗しました。もう一度お試しください。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-red-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;